import fittingsData from '../components/fittingsData';
import {
  DN_TO_PIPE_DIAMETER,
  NAK_GAIKA_TO_PIPE_DIAMETER,
  SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER,
  getMuftaAutoDisplayValue,
  getNippelAutoDisplayValue,
} from '../components/AutoConstructor/collectorUtils';

function pipeOf(cylinder) {
  const width = Number(cylinder.width) || 0;
  const height = Number(cylinder.height) || 0;
  return {
    id: cylinder.id,
    radius: Math.min(width, height) / 2,
    x: Number(cylinder.x) || 0,
    y: Number(cylinder.y) || 0,
    width,
    height,
    centerX: (Number(cylinder.x) || 0) + width / 2,
    centerY: (Number(cylinder.y) || 0) + height / 2,
    square: cylinder.pipeSpec?.shape === 'square',
    axis: null,
  };
}

/** Короткая врезка короче своего диаметра тоже лежит вдоль листа, если упирается в стояк. */
function chooseAxis(pipe, pipes) {
  const besideRiser = pipes.some((other) => (
    other.id !== pipe.id
    && other.height > other.width + 80
    && other.height > pipe.height + 20
    && pipe.centerY >= other.y - 4
    && pipe.centerY <= other.y + other.height + 4
    && Math.abs(pipe.centerX - other.centerX) > other.width * 0.35
    && Math.abs(pipe.centerX - other.centerX) < other.width * 0.5 + Math.max(pipe.width, pipe.height) + 30
  ));
  if (besideRiser && pipe.width >= pipe.height * 0.45) return 'x';
  if (pipe.width > pipe.height + 1) return 'x';
  return 'y';
}

/** Доводим торец до оси стояка, чтобы врезка входила в трубу, а не обрывалась у контура. */
function extendedSpan(pipe, pipes) {
  let x0 = pipe.x;
  let x1 = pipe.x + pipe.width;
  pipes.forEach((other) => {
    if (other.id === pipe.id || other.axis !== 'y') return;
    if (pipe.centerY < other.y - 4 || pipe.centerY > other.y + other.height + 4) return;
    const limit = other.radius + Math.max(pipe.height, 40);
    if (Math.abs(x0 - other.centerX) <= limit) x0 = Math.min(x0, other.centerX);
    if (Math.abs(x1 - other.centerX) <= limit) x1 = Math.max(x1, other.centerX);
  });
  return { x: x0, w: Math.max(1, x1 - x0) };
}

function typedStick(fitting) {
  const key = fitting.itemKey || fitting.key || '';
  const special = /^(Flanec_|Nak_Gaika_|Shaib_Nar_Rezba_)/.test(key);
  const raw = special
    ? (fitting.flangePipeLength ?? fitting.fittingLength)
    : fitting.overrideText;
  const value = Number(String(raw ?? '').replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** Видимый вылет от наружной стенки, как на боковом выходе. */
function visibleStickOut(fitting, pipe) {
  const key = fitting.itemKey || fitting.key || '';
  const typed = typedStick(fitting);
  const info = fittingsData[key];
  const shape = pipe.square ? 'square' : 'round';
  if (key.startsWith('Flanec_')) return typed ?? 100;
  if (key.startsWith('Nak_Gaika_') || key.startsWith('Shaib_Nar_Rezba_')) {
    return typed ?? (shape === 'square' ? 54 : 32);
  }
  if (key.startsWith('Nippel_')) {
    return typed ?? getNippelAutoDisplayValue(key, pipe.radius * 2, info?.length || 0, shape) ?? (info?.length || 0);
  }
  if (key.startsWith('Mufta_')) {
    return typed ?? getMuftaAutoDisplayValue(key, info?.length || 0, shape) ?? (info?.length || 0);
  }
  return typed ?? (info?.length || 0);
}

function planRotation(rotation) {
  const turn = ((Number(rotation) || 0) % 360 + 360) % 360;
  return (turn * Math.PI) / 180;
}

/** 0 и 180 — ось вдоль взгляда, на виде остаётся торец. 90 и 270 — ось вдоль листа. */
function axisAlongView(rotation) {
  const turn = ((Number(rotation) || 0) % 360 + 360) % 360;
  return turn < 45 || turn >= 315 || (turn >= 135 && turn < 225);
}

function outletBreadth(key) {
  if (key.startsWith('Flanec_')) {
    const dn = (key.match(/Flanec_(\d+)/) || [])[1];
    return DN_TO_PIPE_DIAMETER[dn] || 30;
  }
  if (key.startsWith('Nak_Gaika_')) return NAK_GAIKA_TO_PIPE_DIAMETER[key.slice('Nak_Gaika_'.length)] || 30;
  if (key.startsWith('Shaib_Nar_Rezba_')) return SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[key.slice('Shaib_Nar_Rezba_'.length)] || 30;
  return fittingsData[key]?.diameter || 30;
}

/**
 * Вид сверху одним снимком: прямоугольники и несколько размеров.
 * Горизонталь вида — вдоль листа, вертикаль — вперёд/назад.
 * Ближние к верху листа трубы рисуются последними и закрывают дальние.
 */
function distanceToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  const param = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return Math.hypot(px - (x1 + param * dx), py - (y1 + param * dy));
}

/** Прямая стенка — одна длинная привязка. Дуга — четыре крайние точки, как стороны на главном виде. */
function snapEdgesForLine(pts, polyIndex) {
  const count = pts.length / 2;
  if (count < 2) return [];
  const x1 = pts[0];
  const y1 = pts[1];
  const x2 = pts[pts.length - 2];
  const y2 = pts[pts.length - 1];
  const length = Math.hypot(x2 - x1, y2 - y1);
  let straight = length > 8;
  if (straight) {
    for (let i = 2; i < pts.length - 2; i += 2) {
      if (distanceToSegment(pts[i], pts[i + 1], x1, y1, x2, y2) > 1.2) {
        straight = false;
        break;
      }
    }
  }
  if (straight) {
    return [{
      polyIndex,
      type: Math.abs(y2 - y1) >= Math.abs(x2 - x1) ? 'left' : 'top',
      x1,
      y1,
      x2,
      y2,
    }];
  }
  let left = null;
  let right = null;
  let top = null;
  let bottom = null;
  for (let i = 0; i < pts.length; i += 2) {
    const x = pts[i];
    const y = pts[i + 1];
    if (!left || x < left.x) left = { x, y };
    if (!right || x > right.x) right = { x, y };
    if (!top || y < top.y) top = { x, y };
    if (!bottom || y > bottom.y) bottom = { x, y };
  }
  const tick = 8;
  return [
    { polyIndex, type: 'left', x1: left.x, y1: left.y - tick, x2: left.x, y2: left.y + tick, ax: left.x, ay: left.y },
    { polyIndex, type: 'right', x1: right.x, y1: right.y - tick, x2: right.x, y2: right.y + tick, ax: right.x, ay: right.y },
    { polyIndex, type: 'top', x1: top.x - tick, y1: top.y, x2: top.x + tick, y2: top.y, ax: top.x, ay: top.y },
    { polyIndex, type: 'bottom', x1: bottom.x - tick, y1: bottom.y, x2: bottom.x + tick, y2: bottom.y, ax: bottom.x, ay: bottom.y },
  ];
}

/**
 * Точки привязки вида, как зелёные точки на главном виде: углы, концы линий,
 * середины длинных прямых, у дуг и окружностей крайние точки, у окружности ещё и центр.
 */
function computeGenericPoints(polylines, points) {
  const add = (x, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (points.some((point) => Math.hypot(point.x - x, point.y - y) < 1.2)) return;
    points.push({ x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 });
  };
  (polylines || []).forEach((pts) => {
    if (!pts || pts.length < 4) return;
    const count = pts.length / 2;
    const closed = Math.hypot(pts[0] - pts[pts.length - 2], pts[1] - pts[pts.length - 1]) < 1.5;
    if (closed && count >= 8) {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (let i = 0; i < pts.length; i += 2) {
        minX = Math.min(minX, pts[i]);
        maxX = Math.max(maxX, pts[i]);
        minY = Math.min(minY, pts[i + 1]);
        maxY = Math.max(maxY, pts[i + 1]);
      }
      const width = maxX - minX;
      const height = maxY - minY;
      if (width > 4 && Math.abs(width - height) <= Math.max(1.5, width * 0.1)) {
        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;
        const radius = (width + height) / 4;
        let roundLoop = true;
        for (let i = 0; i < pts.length; i += 2) {
          if (Math.abs(Math.hypot(pts[i] - cx, pts[i + 1] - cy) - radius) > radius * 0.1 + 0.6) {
            roundLoop = false;
            break;
          }
        }
        if (roundLoop) {
          add(cx, cy - radius);
          add(cx - radius, cy);
          add(cx + radius, cy);
          add(cx, cy + radius);
          add(cx, cy);
          return;
        }
      }
    }
    let curved = false;
    for (let i = 1; i < count - 1; i += 1) {
      const ax = pts[i * 2] - pts[(i - 1) * 2];
      const ay = pts[i * 2 + 1] - pts[(i - 1) * 2 + 1];
      const bx = pts[(i + 1) * 2] - pts[i * 2];
      const by = pts[(i + 1) * 2 + 1] - pts[i * 2 + 1];
      const la = Math.hypot(ax, ay);
      const lb = Math.hypot(bx, by);
      if (la < 0.3 || lb < 0.3) continue;
      const cos = Math.max(-1, Math.min(1, (ax * bx + ay * by) / (la * lb)));
      const turn = (Math.acos(cos) * 180) / Math.PI;
      if (turn > 35) add(pts[i * 2], pts[i * 2 + 1]);
      else if (turn > 2.5) curved = true;
    }
    if (!closed) {
      add(pts[0], pts[1]);
      add(pts[pts.length - 2], pts[pts.length - 1]);
    }
    if (curved) {
      let left = null;
      let right = null;
      let top = null;
      let bottom = null;
      for (let i = 0; i < pts.length; i += 2) {
        const x = pts[i];
        const y = pts[i + 1];
        if (!left || x < left.x) left = { x, y };
        if (!right || x > right.x) right = { x, y };
        if (!top || y < top.y) top = { x, y };
        if (!bottom || y > bottom.y) bottom = { x, y };
      }
      [left, right, top, bottom].forEach((point) => add(point.x, point.y));
      const width = right.x - left.x;
      const height = bottom.y - top.y;
      if (closed && width > 4 && Math.abs(width - height) <= Math.max(2, width * 0.12)) {
        add((left.x + right.x) / 2, (top.y + bottom.y) / 2);
      }
    } else if (count === 2) {
      const length = Math.hypot(pts[2] - pts[0], pts[3] - pts[1]);
      if (length >= 24) add((pts[0] + pts[2]) / 2, (pts[1] + pts[3]) / 2);
    }
  });
}

/** Полная окружность: центр и радиус. Нужно, чтобы отделить внутренний круг стенки. */
function circleOf(pts) {
  if (!pts || pts.length < 16) return null;
  if (Math.hypot(pts[0] - pts[pts.length - 2], pts[1] - pts[pts.length - 1]) > 1.5) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < pts.length; i += 2) {
    minX = Math.min(minX, pts[i]);
    maxX = Math.max(maxX, pts[i]);
    minY = Math.min(minY, pts[i + 1]);
    maxY = Math.max(maxY, pts[i + 1]);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  if (width < 4 || Math.abs(width - height) > Math.max(1.5, width * 0.08)) return null;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const radius = (width + height) / 4;
  for (let i = 0; i < pts.length; i += 2) {
    if (Math.abs(Math.hypot(pts[i] - cx, pts[i + 1] - cy) - radius) > radius * 0.08 + 0.6) return null;
  }
  return { cx, cy, radius };
}

/** Внутренние круги стенки. Радиусы приходят с вида, точки к ним не ставим. */
function withoutInnerWallCircles(lines, bores) {
  const radii = (bores || []).map(Number).filter((value) => value > 1);
  if (radii.length === 0) return lines;
  return lines.filter((pts) => {
    const circle = circleOf(pts);
    if (!circle) return true;
    return !radii.some((radius) => Math.abs(circle.radius - radius) < 0.8);
  });
}

function groupBox(lines) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  lines.forEach((pts) => {
    for (let i = 0; i < pts.length; i += 2) {
      minX = Math.min(minX, pts[i]);
      maxX = Math.max(maxX, pts[i]);
      minY = Math.min(minY, pts[i + 1]);
      maxY = Math.max(maxY, pts[i + 1]);
    }
  });
  return { x0: minX, y0: minY, x1: maxX, y1: maxY };
}

/**
 * Бортшайба: углы и середины боковых сторон. Фланец: середины левой и правой стороны.
 * Лицевые муфты и ниппели: середины торца и стенки (диаметры) и середины боков.
 * Остальное: углы, концы, середины длинных прямых, у окружностей четыре стороны и центр.
 */
export function computeViewSnapPoints(polylines, groups) {
  const points = [];
  const addUnique = (x, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (points.some((point) => Math.hypot(point.x - x, point.y - y) < 1.2)) return;
    points.push({ x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 });
  };
  if (!Array.isArray(groups) || groups.length === 0) {
    computeGenericPoints(polylines, points);
    return points;
  }
  groups.forEach((group) => {
    const lines = (polylines || []).slice(group.start, group.end);
    if (lines.length === 0) return;
    const box = groupBox(lines);
    const midX = (box.x0 + box.x1) / 2;
    const midY = (box.y0 + box.y1) / 2;
    if (group.role === 'flange') {
      addUnique(box.x0, midY);
      addUnique(box.x1, midY);
    } else if (group.role === 'collar') {
      addUnique(box.x0, box.y0);
      addUnique(box.x0, box.y1);
      addUnique(box.x1, box.y0);
      addUnique(box.x1, box.y1);
      addUnique(box.x0, midY);
      addUnique(box.x1, midY);
    } else if (group.role === 'facing') {
      addUnique(box.x0, box.y0);
      addUnique(box.x1, box.y0);
      addUnique(box.x0, box.y1);
      addUnique(box.x1, box.y1);
      addUnique(midX, box.y0);
      addUnique(midX, box.y1);
      addUnique(box.x0, midY);
      addUnique(box.x1, midY);
    } else if (group.role === 'squareCap') {
      // Углы острого прямоугольника, без точек на дуге скругления, и середина каждой стенки.
      addUnique(box.x0, box.y0);
      addUnique(box.x1, box.y0);
      addUnique(box.x0, box.y1);
      addUnique(box.x1, box.y1);
      addUnique(midX, box.y0);
      addUnique(midX, box.y1);
      addUnique(box.x0, midY);
      addUnique(box.x1, midY);
    } else if (group.role === 'thread' || group.role === 'fastener' || group.role === 'floor') {
      // Резьба и крепеж — рисунок. У крепежа одна линия по низу, её добавляет раскладка вида.
    } else if (group.role === 'pipe') {
      const local = [];
      computeGenericPoints(lines, local);
      local.forEach((point) => addUnique(point.x, point.y));
    } else {
      const local = [];
      computeGenericPoints(withoutInnerWallCircles(lines, group.bores), local);
      local.forEach((point) => addUnique(point.x, point.y));
    }
  });
  return points;
}

const snapPointCache = new WeakMap();

/** Точки вида. Для сохранённых раньше видов считаются один раз. */
export function getViewSnapPoints(scene) {
  if (!scene) return [];
  if (scene.pointsVersion === 2 && Array.isArray(scene.points)) return scene.points;
  if (!snapPointCache.has(scene)) snapPointCache.set(scene, computeViewSnapPoints(scene.polylines, scene.groups));
  return snapPointCache.get(scene);
}

/**
 * Плоскость стены: кромка крепежа, которая дальше всех от труб.
 * У настенного YKRS это тыл планки. У напольного обе стороны примерно одинаково далеко, линию не ставим.
 */
function wallContact(fastenerLines, otherLines, viewKind) {
  const depthHorizontal = viewKind === 'left' || viewKind === 'right';
  const depthVertical = viewKind === 'top' || viewKind === 'bottom';
  if (!depthHorizontal && !depthVertical) return null;
  const box = groupBox(fastenerLines);
  const other = groupBox(otherLines);
  if (!Number.isFinite(box.x0) || !Number.isFinite(other.x0)) return null;
  const mid = depthHorizontal ? (other.x0 + other.x1) / 2 : (other.y0 + other.y1) / 2;
  const lo = depthHorizontal ? box.x0 : box.y0;
  const hi = depthHorizontal ? box.x1 : box.y1;
  const far = Math.max(Math.abs(lo - mid), Math.abs(hi - mid));
  const near = Math.min(Math.abs(lo - mid), Math.abs(hi - mid));
  if (far - near < 40) return null;
  const coord = Math.abs(lo - mid) >= Math.abs(hi - mid) ? lo : hi;
  const along = [];
  fastenerLines.forEach((pts) => {
    for (let i = 0; i < pts.length; i += 2) {
      const value = depthHorizontal ? pts[i] : pts[i + 1];
      if (Math.abs(value - coord) <= 2) along.push(depthHorizontal ? pts[i + 1] : pts[i]);
    }
  });
  if (along.length < 2) return null;
  const from = Math.min(...along);
  const to = Math.max(...along);
  if (to - from < 8) return null;
  const middle = (from + to) / 2;
  if (depthHorizontal) {
    return { x1: coord, y1: from, x2: coord, y2: to, x: coord, y: middle, vertical: true };
  }
  return { x1: from, y1: coord, x2: to, y2: coord, x: middle, y: coord, vertical: false };
}

/** Точка опоры одной ножки: самая нижняя точка и короткая кромка вокруг неё. */
function footContact(lines) {
  let tip = null;
  lines.forEach((pts) => {
    for (let i = 0; i < pts.length; i += 2) {
      const x = pts[i];
      const y = pts[i + 1];
      if (!tip || y > tip.y || (y === tip.y && x < tip.x)) tip = { x, y };
    }
  });
  if (!tip) return null;
  const xs = [tip.x];
  lines.forEach((pts) => {
    for (let i = 0; i < pts.length; i += 2) {
      if (Math.abs(pts[i + 1] - tip.y) <= 6 && Math.abs(pts[i] - tip.x) <= 28) xs.push(pts[i]);
    }
  });
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const width = Math.max(right - left, 16);
  const mid = (left + right) / 2;
  return { x: tip.x, y: tip.y, x1: mid - width / 2, x2: mid + width / 2 };
}

export function layoutProjection(polylines, rawGroups = [], viewKind = '') {
  const lines = [];
  const indexMap = [];
  (polylines || []).forEach((line) => {
    if (!line || line.length < 4) {
      indexMap.push(-1);
      return;
    }
    indexMap.push(lines.length);
    lines.push(line);
  });
  if (lines.length === 0) return null;
  const groups = (rawGroups || []).map((group) => {
    let start = -1;
    let end = -1;
    const from = Number(group.start) || 0;
    const to = Number(group.end) || 0;
    for (let i = from; i < to && i < indexMap.length; i += 1) {
      if (indexMap[i] < 0) continue;
      if (start < 0) start = indexMap[i];
      end = indexMap[i] + 1;
    }
    return start < 0 ? null : { ...group, start, end };
  }).filter(Boolean);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  lines.forEach((pts) => {
    for (let i = 0; i < pts.length; i += 2) {
      minX = Math.min(minX, pts[i]);
      maxX = Math.max(maxX, pts[i]);
      minY = Math.min(minY, pts[i + 1]);
      maxY = Math.max(maxY, pts[i + 1]);
    }
  });
  const pad = 28;
  const shiftX = pad - minX;
  const shiftY = pad - minY;
  const shifted = lines.map((pts) => pts.map((value, index) => (
    value + (index % 2 === 0 ? shiftX : shiftY)
  )));
  const edges = [];
  const fastenerSpan = (groups || []).filter((group) => group.role === 'fastener');
  const floorSpan = (groups || []).filter((group) => group.role === 'floor');
  const isFastenerLine = (index) => (
    fastenerSpan.some((group) => index >= group.start && index < group.end)
    || floorSpan.some((group) => index >= group.start && index < group.end)
  );
  const floorBoxes = floorSpan.map((group) => groupBox(shifted.slice(group.start, group.end)));
  const onStand = (x, y) => floorBoxes.some((box) => (
    x >= box.x0 - 16 && x <= box.x1 + 16 && y >= box.y0 - 28 && y <= box.y1 + 6
  ));
  shifted.forEach((pts, index) => {
    if (isFastenerLine(index)) return;
    snapEdgesForLine(pts, index).forEach((edge) => {
      const midX = (edge.x1 + edge.x2) / 2;
      const midY = (edge.y1 + edge.y2) / 2;
      if (onStand(midX, midY)) return;
      edges.push(edge);
    });
  });
  const quietLines = [];
  const wallLines = [];
  const wallPoints = [];
  const otherLines = shifted.filter((_, index) => !isFastenerLine(index));
  fastenerSpan.forEach((group) => {
    const wall = wallContact(shifted.slice(group.start, group.end), otherLines, viewKind);
    if (!wall) return;
    const polyIndex = shifted.length;
    shifted.push([wall.x1, wall.y1, wall.x2, wall.y2]);
    quietLines.push(polyIndex);
    wallLines.push(polyIndex);
    wallPoints.push({ x: Math.round(wall.x * 100) / 100, y: Math.round(wall.y * 100) / 100, guide: true });
    edges.push({
      polyIndex,
      type: wall.vertical ? 'left' : 'top',
      guide: true,
      x1: wall.x1,
      y1: wall.y1,
      x2: wall.x2,
      y2: wall.y2,
      ax: wall.x,
      ay: wall.y,
    });
  });
  const sideView = viewKind === 'left' || viewKind === 'right';
  if (sideView) {
    fastenerSpan.forEach((group) => {
      const part = shifted.slice(group.start, group.end);
      const box = groupBox(part);
      if (!Number.isFinite(box.y1) || box.x1 - box.x0 < 1) return;
      const foot = footContact(part);
      if (!foot) return;
      const polyIndex = shifted.length;
      shifted.push([foot.x1, foot.y, foot.x2, foot.y]);
      quietLines.push(polyIndex);
      edges.push({
        polyIndex,
        type: 'bottom',
        guide: true,
        x1: foot.x1,
        y1: foot.y,
        x2: foot.x2,
        y2: foot.y,
        ax: foot.x,
        ay: foot.y,
      });
    });
  }
  return {
    width: maxX - minX + pad * 2,
    height: maxY - minY + pad * 2 + 22,
    polylines: shifted,
    edges,
    quietLines,
    wallLines,
    points: (() => {
      const points = computeViewSnapPoints(shifted, groups);
      wallPoints.forEach((point) => points.push(point));
      if (sideView) {
        fastenerSpan.forEach((group) => {
          const foot = footContact(shifted.slice(group.start, group.end));
          if (!foot) return;
          points.push({ x: Math.round(foot.x * 100) / 100, y: Math.round(foot.y * 100) / 100, guide: true });
        });
      }
      if (floorSpan.length === 0) return points;
      const floorBoxes = floorSpan.map((group) => groupBox(shifted.slice(group.start, group.end)));
      const onStand = (x, y) => floorBoxes.some((box) => (
        x >= box.x0 - 16 && x <= box.x1 + 16 && y >= box.y0 - 28 && y <= box.y1 + 6
      ));
      return points.filter((point) => !onStand(point.x, point.y));
    })(),
    pointsVersion: 2,
    groups,
    shapes: [],
    dims: [],
  };
}

export function buildTopView(cylinders, fittings) {
  const pipes = (cylinders || []).filter((item) => item && !item.isHidden && item.width > 1 && item.height > 1).map(pipeOf);
  if (pipes.length === 0) return null;
  pipes.forEach((pipe) => { pipe.axis = chooseAxis(pipe, pipes); });

  const bodies = [];
  const parts = [];
  const dims = [];

  pipes.forEach((pipe) => {
    if (pipe.axis === 'x') {
      const span = extendedSpan(pipe, pipes);
      bodies.push({ depth: pipe.centerY, shape: { kind: 'rect', x: span.x, y: -pipe.radius, w: span.w, h: pipe.radius * 2 } });
      return;
    }
    const shape = pipe.square
      ? { kind: 'rect', x: pipe.centerX - pipe.radius, y: -pipe.radius, w: pipe.radius * 2, h: pipe.radius * 2 }
      : { kind: 'circle', x: pipe.centerX, y: 0, r: pipe.radius };
    // Стояки и патрубки вдоль взгляда рисуем поверх полос, иначе ближняя труба их стирает.
    parts.push({ depth: pipe.centerY, shape });
  });

  (fittings || []).forEach((fit) => {
    if (!fit || fit.isHidden || fit.type === 'fastener') return;
    const key = fit.itemKey || fit.key || '';
    const width = Number(fit.width) || 0;
    const height = Number(fit.height) || 0;
    if (width < 2 || height < 0.5) return;
    const anchorX = fit.groupX != null ? Number(fit.groupX) : (Number(fit.x) || 0) + width / 2;
    const anchorY = fit.groupY != null ? Number(fit.groupY) : (Number(fit.y) || 0) + height / 2;
    if (fit.isSelfFacing) {
      const pipe = pipes.find((item) => item.id === fit.igesParentCylinderId);
      if (!pipe) return;
      const side = fit.side === 'back' ? -1 : 1;
      const stick = visibleStickOut(fit, pipe);
      if (!(stick > 1)) return;
      const breadth = Math.max(8, outletBreadth(key));
      const station = pipe.axis === 'x' ? anchorX : pipe.centerX;
      const left = station - breadth / 2;
      const z0 = side * pipe.radius;
      const z1 = side * (pipe.radius + stick);
      parts.push({
        depth: pipe.centerY - 0.01,
        shape: {
        kind: 'rect',
        x: left,
        y: Math.min(z0, z1),
        w: breadth,
        h: Math.abs(z1 - z0),
        },
      });
      dims.push({
        depth: pipe.centerY - 0.01,
        x: left + breadth + 14,
        y1: z0,
        y2: z1,
        text: String(Math.round(stick)),
      });
      return;
    }
    if (axisAlongView(fit.rotation)) {
      // Эллиптическая заглушка на торце стояка сверху видна тем же кругом, что и труба.
      if (key.startsWith('Zaglushka')) return;
      const host = pipes.find((pipe) => pipe.axis === 'y' && Math.abs(anchorX - pipe.centerX) <= 8);
      if (host && Math.abs(width / 2 - host.radius) <= Math.max(4, host.radius * 0.2)) return;
      parts.push({ depth: anchorY, shape: { kind: 'circle', x: anchorX, y: 0, r: width / 2 } });
      return;
    }
    const angle = planRotation(fit.rotation);
    const reach = Math.sin(angle) * height;
    if (key.startsWith('Zaglushka_EL')) {
      parts.push({
        depth: anchorY,
        shape: { kind: 'circle', x: anchorX + reach, y: 0, r: width / 2 },
      });
      return;
    }
    const left = Math.min(anchorX, anchorX + reach);
    parts.push({
      depth: anchorY,
      shape: {
        kind: 'rect',
        x: left,
        y: -width / 2,
        w: Math.abs(reach),
        h: width,
      },
    });
  });

  const order = (list) => list.sort((a, b) => b.depth - a.depth).map((item) => item.shape);
  const shapes = [...order(bodies), ...order(parts)];

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const grow = (x, y, w, h) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  };
  shapes.forEach((shape) => {
    if (shape.kind === 'circle') grow(shape.x - shape.r, shape.y - shape.r, shape.r * 2, shape.r * 2);
    else grow(shape.x, shape.y, shape.w, shape.h);
  });
  dims.forEach((dim) => grow(dim.x - 6, Math.min(dim.y1, dim.y2), 40, Math.abs(dim.y2 - dim.y1)));
  if (!Number.isFinite(minX)) return null;

  const pad = 28;
  const shiftX = pad - minX;
  const shiftY = pad - minY;
  const shifted = shapes.map((shape) => ({ ...shape, x: shape.x + shiftX, y: shape.y + shiftY }));
  const edges = [];
  shifted.forEach((shape) => {
    if (shape.kind === 'circle') {
      const { x, y, r } = shape;
      edges.push(
        { type: 'left', x1: x - r, y1: y - r, x2: x - r, y2: y + r },
        { type: 'right', x1: x + r, y1: y - r, x2: x + r, y2: y + r },
        { type: 'top', x1: x - r, y1: y - r, x2: x + r, y2: y - r },
        { type: 'bottom', x1: x - r, y1: y + r, x2: x + r, y2: y + r },
      );
    } else {
      edges.push(
        { type: 'top', x1: shape.x, y1: shape.y, x2: shape.x + shape.w, y2: shape.y },
        { type: 'bottom', x1: shape.x, y1: shape.y + shape.h, x2: shape.x + shape.w, y2: shape.y + shape.h },
        { type: 'left', x1: shape.x, y1: shape.y, x2: shape.x, y2: shape.y + shape.h },
        { type: 'right', x1: shape.x + shape.w, y1: shape.y, x2: shape.x + shape.w, y2: shape.y + shape.h },
      );
    }
  });
  return {
    width: maxX - minX + pad * 2,
    height: maxY - minY + pad * 2 + 22,
    shapes: shifted,
    edges,
    dims: dims.map((dim) => ({ ...dim, x: dim.x + shiftX, y1: dim.y1 + shiftY, y2: dim.y2 + shiftY })),
  };
}
